import React, { useEffect, useState } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { TenantSocialLinks } from 'generated/api';
import './style.scss';

const SOCIAL_NETWORKS: Array<{ key: keyof TenantSocialLinks; label: string }> = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitter', label: 'Twitter / X' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'linkedin', label: 'LinkedIn' },
];

/**
 * Self-serve tenant options: the tenant-admin-editable subset of tenant
 * configuration. Currently social links (rendered as icons in the email
 * footer); the intended home for future miscellaneous options.
 */
const TenantOptions: React.FC = () => {
  const dispatch = useAppDispatch();
  const [socialLinks, setSocialLinks] = useState<TenantSocialLinks>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState<boolean>(false);

  useEffect(() => {
    loadOptions();
  }, [dispatch]);

  async function loadOptions(): Promise<void> {
    try {
      const response = await dispatch(api.Tenants.getTenantOptions());
      setSocialLinks(response?.social_links ?? {});
    } catch (err) {
      console.error('Failed to load tenant options:', err);
    } finally {
      setLoading(false);
    }
  }

  function updateLink(network: keyof TenantSocialLinks, value: string): void {
    setSaved(false);
    setError(undefined);
    setSocialLinks((prev) => ({ ...prev, [network]: value }));
  }

  async function onSave(): Promise<void> {
    const invalid = SOCIAL_NETWORKS.find(({ key }) => {
      const value = socialLinks[key]?.trim();
      return !!value && !/^https:\/\/\S+\.\S+/.test(value);
    });
    if (invalid) {
      setError(`${invalid.label} must be a full https:// link`);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const response = await dispatch(
        api.Tenants.updateTenantOptions({ tenantOptions: { social_links: socialLinks } }),
      );
      setSocialLinks(response?.social_links ?? {});
      setSaved(true);
    } catch (err) {
      console.error('Failed to save tenant options:', err);
      setError('Failed to save — please try again');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="tenant-options">Loading…</div>;
  }

  return (
    <div className="tenant-options">
      <div className="tenant-options-section">
        <h3>Social Links</h3>
        <p className="tenant-options-hint">
          Shown as icons in the footer of every email sent to your patients. Leave a network blank
          to hide its icon; with none configured the social row is omitted entirely.
        </p>
        {SOCIAL_NETWORKS.map(({ key, label }) => (
          <DefaultTextField
            key={key}
            label={label}
            name={`tenant-options-${key}`}
            placeholder={`https://www.${key === 'twitter' ? 'twitter' : key}.com/yourpage`}
            value={socialLinks[key] ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateLink(key, e.target.value)}
          />
        ))}
        {error && <div className="tenant-options-error">{error}</div>}
        {saved && <div className="tenant-options-saved">Saved</div>}
        <DefaultButton disabled={saving} onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </DefaultButton>
      </div>
    </div>
  );
};

export default TenantOptions;
